export const MAX_ESTIMATED_LIFESPAN_YEARS = 75
export const MAX_PLAUSIBLE_LIVING_AGE = 110

// Pull the nth 4-digit year out of a person's lifespan string (index 0 = birth,
// 1 = death). Returns null when there is no parseable year at that position.
export const getLifespanYear = (person, index) => {
  const yearMatches = `${person?.lifespan ?? person?.lifeSpan ?? ''}`.match(/\d{4}/g) ?? []
  const year = Number.parseInt(yearMatches[index], 10)
  return Number.isFinite(year) ? year : null
}

// estimatedDeathYear: someone known to have died, in an unknown year (a Time Machine guest's
// relative marked deceased). It ends their life without being shown as a recorded death.
const hasEstimatedDeathYear = (person) => Number.isFinite(person?.estimatedDeathYear)

const isPlausiblyStillLiving = (person) => {
  if (!Number.isFinite(person?.birthYear)) return false
  if (Number.isFinite(person?.deathYear)) return false
  if (hasEstimatedDeathYear(person)) return false
  return new Date().getFullYear() - person.birthYear <= MAX_PLAUSIBLE_LIVING_AGE
}

export const getEstimatedDeathYear = (birthYear) => {
  if (!Number.isFinite(birthYear)) return null
  return birthYear + MAX_ESTIMATED_LIFESPAN_YEARS
}

export const hasEstimatedLifespan = (person) => {
  if (!Number.isFinite(person?.birthYear)) return false
  if (person?.living) return false
  if (Number.isFinite(person?.deathYear)) return false
  if (hasEstimatedDeathYear(person)) return true
  // If they were born recently enough to still plausibly be alive, don't pretend we estimated a death year.
  if (isPlausiblyStillLiving(person)) return false
  return true
}

export const getEffectiveDeathYear = (person) => {
  if (Number.isFinite(person?.deathYear)) return person.deathYear
  if (hasEstimatedDeathYear(person) && !person?.living) return person.estimatedDeathYear
  if (hasEstimatedLifespan(person)) return getEstimatedDeathYear(person.birthYear)
  return null
}

export const isAliveInYear = (person, year) => {
  if (!Number.isFinite(year) || !Number.isFinite(person?.birthYear)) return false
  if (person.birthYear > year) return false
  if (person?.living) return true
  // Treat people born within the plausible-living window with no death year as alive.
  if (isPlausiblyStillLiving(person)) return true

  const effectiveDeathYear = getEffectiveDeathYear(person)
  if (Number.isFinite(effectiveDeathYear)) return effectiveDeathYear >= year

  return false
}
